import {Box, LinearProgress, Link} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../../const/i18nNamespaces';
import {
    MEDIC_PROFILE_PAGE,
    MEDIC_SETTINGS,
    NEW_PRESCRIPTION_PAGE,
    NEW_REFERRAL_PAGE,
} from '../../../../const/routes';
import {useContext, useEffect, useState} from 'react';
import {
    useAppDispatch,
    useAppSelector,
} from '../../../../hooks/customReduxHooks';
import useStyles from './ReferralsListStyles';
import useCertificateThumbprint from '../../../../hooks/certificate/useCertificateThumbprint';
import {selectAuthUserData} from '../../../../store/auth';
import {Loading} from '../../../index';
import {useHistory} from 'react-router';
import { fetchReferrals, selectReferrals, selectReferralsStatus } from '../../../../store/referralSlice';
import ReferralsListItem from './ReferralsListItem/ReferralsListItem';
import filesDownloadProvider from '../../../../services/filesDownloadProvider';
import { referralApi } from '../../../../api/referral/referralApi';
import { getReferralFileName } from '../../../../utils/referral';
import axios from 'axios';
import { AlertContext } from '../../../../contexts/AlertContext';
import { openBlobFromUrlInNewTab } from '../../../../utils/file';

type PrescriptionListProps = {
    appointmentId: number;
};

const ReferralsList = ({appointmentId}: PrescriptionListProps) => {
    const {t} = useTranslation(i18nNamespaces.REFERRAL);
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectReferralsStatus);
    const referrals = useAppSelector(selectReferrals);
    const classes = useStyles();
    const userData = useAppSelector(selectAuthUserData);
    const history = useHistory();
    const {loading: thumbprintLoading, thumbprint} = useCertificateThumbprint(
        userData?.userId,
    );
    const [loading, setLoading] = useState(false);
    const openAlert = useContext(AlertContext);
    const {t: tCommon} = useTranslation(i18nNamespaces.COMMON);
    const {t: tErrors} = useTranslation(i18nNamespaces.ERRORS);

    const getReferrals = () =>
        dispatch(fetchReferrals({appointmentId}));

    useEffect(() => {
        getReferrals();

        // Fetch referrals on tab focus
        window.addEventListener('focus', getReferrals);

        return () => {
            window.removeEventListener('focus', getReferrals);
        };
    }, []);

    const handleClickIssueReferral = () => {
        // Open page in new tab
        const win = window.open(
            `${NEW_REFERRAL_PAGE}/${appointmentId}`,
            '_blank',
        );
        win.focus();
    };

    const handleClickIssuePrescriptionFromDraft = () => {
        // Open page in new tab
        const win = window.open(
            `${NEW_PRESCRIPTION_PAGE}/${appointmentId}?fromDraft=true`,
            '_blank',
        );
        win.focus();
    };

    const handleDownloadReferal = async (referralId: string) => {
        try {
            setLoading(true);
            const {data: url} = await referralApi.getReferralHtml(
                referralId,
            );
            await openBlobFromUrlInNewTab(url);
        } catch (error) {
            if (axios.isAxiosError(error) &&
                error.response?.status === 404)
                openAlert({
                    severity: 'error',
                    message: tCommon('fileNotGenerated'),
                });
            else
                openAlert({
                    severity: 'error',
                    message: tErrors('downloadReferralError'),
                });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h4 className={classes.header}>{t('referrals')}</h4>
            <Loading loading={thumbprintLoading || loading} withBackdrop />
            {status === 'loading' && <LinearProgress color={'secondary'} />}
            <Box className={classes.prescriptionsList}>
                {referrals?.map((referral, idx) => 
                    <ReferralsListItem
                        index={idx}
                        key={referral.id}
                        referral={referral}
                        onClickDownload={handleDownloadReferal}
                    />
                )}
            </Box>
            {!thumbprintLoading && (
                thumbprint ? (
                <Link
                    color={'secondary'}
                    style={{cursor: 'pointer'}}
                    onClick={handleClickIssueReferral}
                >
                    {t('issueAReferral')}
                </Link>
            ) : (
                <Link
                    color={'secondary'}
                    className={classes.loadCertificateLink}
                    onClick={() =>
                        history.push(MEDIC_SETTINGS + MEDIC_PROFILE_PAGE)
                    }
                >
                    {t('loadCertificate')}
                </Link>
            )
            )}
        </>
    );
};

export default ReferralsList;
